/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DropdownComponent } from './dropdown.component';

@Directive({
    selector: '[ngfDropdownTrigger]',
    standalone: false
})
export class DropdownTriggerDirective implements OnChanges, OnDestroy {
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('ngfDropdownTrigger')
  dropdownComponent: DropdownComponent;

  @HostBinding('attr.aria-expanded')
  get ariaExpanded(): string | undefined {
    return this.dropdownComponent?.isOpen ? 'true' : (this.dropdownComponent ? 'false' : undefined);
  }

  @HostBinding('attr.aria-haspopup')
  get ariaHasPopup(): string {
    return 'listbox';
  }

  @HostBinding('attr.aria-controls')
  get ariaControls(): string | undefined {
    return this.dropdownComponent?.dropdownId;
  }

  private readonly destroyed$ = new Subject<void>();
  private subscribedDropdown?: DropdownComponent;

  constructor(private el: ElementRef, private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dropdownComponent'] && this.dropdownComponent !== this.subscribedDropdown) {
      this.subscribeToDropdownEvents();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  @HostListener('click')
  onClick(): void {
    this.dropdownComponent.triggerRef = this.el;
    this.dropdownComponent.toggle();
  }

  open(): void {
    this.dropdownComponent.triggerRef = this.el;
    this.dropdownComponent.open();
  }

  private subscribeToDropdownEvents(): void {
    this.destroyed$.next();
    this.subscribedDropdown = this.dropdownComponent;
    if (!this.dropdownComponent) {
      return;
    }
    this.dropdownComponent.dropdownOpened
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.cdr.markForCheck());
    this.dropdownComponent.dropdownClosed
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.cdr.markForCheck());
  }
}
